import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";

import { DISCRIMINATORS } from "@/redux/modules/api/data/cart/cart.discriminators";
import { generateDefaultSelectors } from "@/redux/selectors.utils";
import { IState } from "@/redux/store.types";
import { getApiDataState } from "@/redux/utils";

import { $CartModuleName } from "./@types/cart.types";

const { isLoading } = generateDefaultSelectors($CartModuleName);

const getCart = (state: IState) =>
    getApiDataState(state, $CartModuleName)?.data;

const isLoadingCart = (state: IState): boolean => {
    return !!getApiDataState(state, $CartModuleName)?.[DISCRIMINATORS.Load]
        ?.loading;
};

const getCartError = (state: IState) => {
    return getApiDataState(state, $CartModuleName)?.[DISCRIMINATORS.Load]
        ?.error;
};

const isStartingCheckout = (state: IState): boolean => {
    return !!getApiDataState(state, $CartModuleName)?.[
        DISCRIMINATORS.StartCheckout
    ]?.loading;
};

const isLoadingPromotionCode = (state: IState): boolean => {
    return !!getApiDataState(state, $CartModuleName)?.[
        DISCRIMINATORS.PromotionCode
    ]?.loading;
};

const isSuccessfullyLoaded = (state: IState): boolean => {
    return (
        !isLoading(state) &&
        !getCartError(state) &&
        !!getApiDataState(state, $CartModuleName)?.data
    );
};

const isCartSuccessfullyLoaded = (state: IState): boolean => {
    return (
        !isLoadingCart(state) &&
        !getCartError(state) &&
        !!getApiDataState(state, $CartModuleName)?.data
    );
};

const hasHomeDelivery = (state: IState) =>
    getCart(state)?.items.some(
        (i) => i.deliveryType === DeliveryType.HomeDelivery
    );

const hasClickAndCollect = (state: IState) => {
    return getCart(state)?.items.some(
        (i) => i.deliveryType === DeliveryType.ClickAndCollect
    );
};

const isClickAndCollectOnly = (state: IState) => {
    return hasClickAndCollect(state) && !hasHomeDelivery(state);
};

const getCartIdForSku = (sku: string) => (state: IState) => {
    return getCart(state)?.items.find((i) => i.sku === sku)?.id;
};

const getAdditionalServicesForCartId =
    (cartId?: string | null) => (state: IState) => {
        if (!cartId) {
            return [];
        }
        const cartItems = getCart(state)?.items;
        const additionalServices = cartItems?.find(
            (i) => i.id === cartId
        )?.additionalServices;

        return (cartItems || []).filter((c) =>
            additionalServices?.some((s) => s.sku === c.sku)
        );
    };

export {
    getAdditionalServicesForCartId,
    getCart,
    getCartError,
    getCartIdForSku,
    hasClickAndCollect,
    hasHomeDelivery,
    isCartSuccessfullyLoaded,
    isClickAndCollectOnly,
    isLoading,
    isLoadingCart,
    isLoadingPromotionCode,
    isStartingCheckout,
    isSuccessfullyLoaded
};
